import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useIconUrl from '../../utils/hooks/useIconUrl';
import useLocalize from '../../utils/hooks/useLocalize';
import { CANDIDATE_COUNT } from '../../utils/scanCandidates';
import AddToMarketBoardModal from '../marketBoard/AddToMarketBoardModal';
import { renderCountChunk } from './aiScannerChunks';
import StockCard from './StockCard';

/**
 * STORY-013: the results step, appended to the conversation once both
 * investment questions are answered — confirmation, count, AI summary, candidate
 * cards, favorites button and disclaimer. Each card's logo is the Wharf icon
 * built here from the current token (STORY-011).
 *
 * #736: the live candidates arrive ALREADY RANKED and resolved. The ranking
 * runs once per round in `AiScannerContainer` (`selectCandidates`) and is
 * passed down, rather than being recomputed here — the same candidates go to
 * the summarizer and onto these cards, so the summary can never describe a
 * different five than the member is looking at.
 *
 * That also converges the live and restored paths onto one shape: a candidate
 * is `{ symbol, companyName }` either way, so the card body no longer branches
 * on where the name came from.
 *
 * @param {Object} props
 * @param {Array<{symbol: string, companyName: string}>} [props.candidates] -
 *   Live ranked candidates (ignored when `overrideResults` is provided).
 * @param {Array<{symbol: string, companyName: string, shortDescription: string}>}
 *   [props.overrideResults] - STORY (#725): a persisted `SearchRound.results`
 *   array. When provided, the card list is built directly from it —
 *   `companyName`/`shortDescription` used as-is, with no `state.symbols.data`
 *   lookup — so restoring never re-fetches or recomputes anything (FR-009).
 * @param {string} [props.overrideSummaryText] - STORY (#725): a persisted
 *   round's `aiSummary`, rendered in place of `state.scans.aiSummary.text`.
 * @returns {JSX.Element}
 */
export const ScanResults = ({
  candidates: liveCandidates = [],
  overrideResults,
  overrideSummaryText
}) => {
  const localize = useLocalize();
  const iconUrl = useIconUrl();
  const [selected, setSelected] = useState(() => new Set());
  const [isBoardModalOpen, setIsBoardModalOpen] = useState(false);

  const liveSummaryText = useSelector((state) => state.scans.aiSummary.text);

  const isRestoring = Array.isArray(overrideResults);
  const summaryText = isRestoring ? overrideSummaryText : liveSummaryText;

  // STORY (#725): capped and de-duped to CANDIDATE_COUNT here too, not just
  // at persist time (`buildSearchRound`) — a session saved by code from
  // before that cap existed can still have up to MAX_SUMMARY_SYMBOLS (5)
  // results sitting in a member's localStorage, and a duplicate symbol would
  // otherwise collide on the `key={symbolId}` below.
  const restoredResults = useMemo(() => {
    if (!isRestoring) return null;
    const seen = new Set();
    return overrideResults
      .filter((r) => {
        if (seen.has(r.symbol)) return false;
        seen.add(r.symbol);
        return true;
      })
      .slice(0, CANDIDATE_COUNT);
  }, [isRestoring, overrideResults]);

  const candidates = isRestoring ? restoredResults : liveCandidates;

  // The button reads the selection intersected with the on-screen cards, so a
  // tick left over from a no-longer-rendered card does not keep it enabled.
  const activeSelection = useMemo(
    () =>
      candidates
        .filter(({ symbol }) => selected.has(symbol))
        .map(({ symbol }) => symbol),
    [candidates, selected]
  );

  const handleToggle = useCallback((symbolId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(symbolId)) next.add(symbolId);
      return next;
    });
  }, []);

  const handleOpenBoardModal = useCallback(() => setIsBoardModalOpen(true), []);
  // The ticks are left as they are: dismissing either modal returns the member
  // to the results screen they came from (#730), not to a cleared one.
  const handleCloseBoardModal = useCallback(
    () => setIsBoardModalOpen(false),
    []
  );

  const description = localize('ai.scanner.results.description.placeholder');

  return (
    <div className="ai-scanner-results">
      <div className="ai-scanner-message">
        <p className="ai-scanner-results-confirmation">
          {localize('ai.scanner.results.confirmation')}
        </p>
        <p>
          {localize('ai.scanner.results.count', {
            count: String(candidates.length),
            b: renderCountChunk
          })}
        </p>
        {/* Bedrock is not enabled in QA yet, so an absent summary is normal. */}
        <p className="ai-scanner-results-summary">
          {summaryText || localize('ai.scanner.results.summary.placeholder')}
        </p>
        <p>{localize('ai.scanner.results.helper')}</p>
      </div>
      <ul className="ai-scanner-results-cards">
        {candidates.map((candidate) => (
          <StockCard
            key={candidate.symbol}
            symbolId={candidate.symbol}
            // Both paths resolve the name before they get here — the seam from
            // `state.symbols.data`, the restored round from what it persisted —
            // and both fall back to the symbol id, so no branch is needed.
            name={candidate.companyName || candidate.symbol}
            description={candidate.shortDescription || description}
            logoUrl={iconUrl(candidate.symbol)}
            selected={selected.has(candidate.symbol)}
            onToggle={handleToggle}
          />
        ))}
      </ul>
      <button
        type="button"
        className="ai-scanner-results-register"
        disabled={activeSelection.length === 0}
        onClick={handleOpenBoardModal}
      >
        {localize('ai.scanner.results.register')}
      </button>
      {/* #730: the existing STORY-014 flow, reused as-is — the checked cards
          are handed over as one array so they register in a single write. */}
      <AddToMarketBoardModal
        open={isBoardModalOpen}
        onClose={handleCloseBoardModal}
        symbol={activeSelection}
        className="ai-scanner-add-to-board"
      />
      <p className="ai-scanner-results-disclaimer">
        {localize('ai.scanner.results.disclaimer')}
      </p>
    </div>
  );
};

ScanResults.propTypes = {
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      companyName: PropTypes.string
    })
  ),
  overrideResults: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      companyName: PropTypes.string,
      shortDescription: PropTypes.string
    })
  ),
  overrideSummaryText: PropTypes.string
};

export default ScanResults;
